import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Users, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GroupChatModal = ({ onClose, onCreate }) => {
  const [groupName, setGroupName] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (!groupName.trim() || selectedUsers.length < 2) {
      alert('Please enter a group name and select at least 2 members');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/conversations`, {
        name: groupName,
        is_group: true,
        participant_ids: selectedUsers
      });
      onCreate(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#08080C] rounded-3xl shadow-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto border border-white/10"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-[#00F0FF]" />
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Group Chat
            </h2>
          </div>
          <button
            data-testid="close-group-modal-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Group Name
            </label>
            <input
              data-testid="group-name-input"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Add Members ({selectedUsers.length} selected)
            </label>
            <input
              data-testid="group-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="w-full px-4 py-2 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 mb-3 transition-all"
            />

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  data-testid={`group-user-${user.id}`}
                  onClick={() => toggleUser(user.id)}
                  className={`p-3 rounded-xl flex items-center gap-3 cursor-pointer transition-all ${
                    selectedUsers.includes(user.id)
                      ? 'bg-[#00F0FF]/10 border-2 border-[#00F0FF]'
                      : 'bg-[#0F0F16] border border-white/5 hover:bg-white/5'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-[#0F0F16] border border-white/10">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-bold text-[#00F0FF]">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-white">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  {selectedUsers.includes(user.id) && (
                    <div className="w-6 h-6 rounded-full bg-[#00F0FF] flex items-center justify-center">
                      <Plus className="w-4 h-4 text-black rotate-45" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              data-testid="group-cancel-button"
              onClick={onClose}
              className="flex-1 py-3 rounded-full font-semibold bg-[#0F0F16] text-gray-300 border border-white/10 hover:bg-white/5 hover:text-white transition-all"
            >
              Cancel
            </button>
            <motion.button
              data-testid="group-create-button"
              onClick={handleCreate}
              disabled={loading || !groupName.trim() || selectedUsers.length < 2}
              whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(0,240,255,0.3)' }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-full font-semibold text-black bg-[#00F0FF] shadow-lg shadow-[#00F0FF]/20 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
            >
              <Users className="w-5 h-5" />
              {loading ? 'Creating...' : 'Create Group'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GroupChatModal;